/**
 * Everything the site says about Casper, in one file.
 *
 * Kept out of the components on purpose: this is the part that changes — a
 * new job, another year of a degree — and it should be one edit in one place,
 * not a hunt through JSX.
 *
 * Dates are stored as `{ from, to }` with `to: null` meaning "still going",
 * so nothing has to be rewritten when a job ends.
 *
 * Two of these lists live in JSON next door rather than in this file: the
 * editor at /beheer writes them, and a tool that has to rewrite JavaScript
 * source to change a string is a tool that will eventually mangle one. What
 * stays here is what only a person editing code would ever change.
 */
import skills from "./skills.json";
import education from "./education.json";

/** What I reach for, four groups deep. Edit at /beheer. */
export const SKILLS = skills.skills;

/** Where it started, newest first. Edit at /beheer. */
export const EDUCATION = education.education;

export const ME = {
  name: "Casper De Bock",
  /* Two roles, both current. The order is deliberate: the day job first. */
  role: "Software developer bij KBC",
  second: "en zaakvoerder van zichtbaarstudio",
  place: "Oudenaarde, België",
  email: "casper.debock@gmail.com",
  github: "https://github.com/CasperDeBock",
  linkedin: "https://www.linkedin.com/in/casperdebock/",
};

/* The one line the whole page hangs off. Problem first, never the tech. */
export const HEADLINE = {
  kicker: "Software developer · Oudenaarde",
  title: "Ik bouw dingen die blijven werken.",
  sub: "Overdag bij KBC, ’s avonds voor mijn eigen klanten. Websites en tools die doen wat ze beloven, ook nog na een jaar.",
};

export const WORK = [
  {
    role: "Software Developer",
    org: "KBC",
    kind: "Voltijds",
    from: "2025-10",
    to: null,
    line: "Big data voor een bank. Pipelines die ’s nachts miljoenen rijen verwerken en ’s ochtends moeten kloppen — want er hangt een beslissing aan vast.",
    tags: ["Spark", "Big data", "Data pipelines", "SQL"],
  },
  {
    role: "Zaakvoerder",
    org: "zichtbaarstudio",
    kind: "Bijberoep",
    from: "2025-11",
    to: null,
    line: "Mijn eigen studio. Websites en digitale tools voor kleine ondernemers — van eerste gesprek tot online, en daarna blijven ze werken.",
    tags: ["React", "Node.js", "Design", "Hosting"],
    href: "https://zichtbaarstudio.be",
  },
  {
    role: "Maker",
    org: "bocky",
    kind: "Hobbyproject",
    from: "2026",
    to: null,
    line: "Kleine apps voor kleine problemen. Ik heb er zelf een hoop — en wat ik voor mezelf oplos zet ik publiek, zodat iedereen met hetzelfde probleem er iets aan heeft.",
    tags: ["React", "Supabase", "PWA"],
    href: "https://bocky.be",
  },
];



/* Facts, not adjectives. "Gedreven teamspeler" says nothing. */
export const FACTS = [
  { value: "9+", label: "Sites live gezet" },
  { value: "2021", label: "Eerste betalende klant" },
  /* Derived, so adding a fourth hat somewhere above does not quietly leave a
     "2" sitting on the homepage. */
  { value: String(WORK.length), label: "Petten tegelijk" },
];

/**
 * The line that runs under the hero, revealed word by word on scroll.
 * Split into words in the component; kept as one string here so it reads as a
 * sentence in the file too.
 */
export const QUOTE = {
  text: "Software is pas af als iemand anders er iets aan heeft. Al de rest is een demo.",
  by: "Waar ik me aan hou",
};

/* ------------------------------------------------------------------ dates */

const MONTHS = [
  "januari",
  "februari",
  "maart",
  "april",
  "mei",
  "juni",
  "juli",
  "augustus",
  "september",
  "oktober",
  "november",
  "december",
];

/** "2025-10" → "oktober 2025". A bare year stays a bare year. */
export function niceDate(value) {
  if (!value) return null;
  const [year, month] = String(value).split("-");
  if (!month) return year;
  return `${MONTHS[Number(month) - 1]} ${year}`;
}

/**
 * The line under a role: "oktober 2025 — nu", "2021 — 2024", "sinds 2026".
 *
 * Three shapes, because the data has three. A month-precise start runs to a
 * dash and an end. A bare year with an end is a range. A bare year with no end
 * is something still going, and printing it as a lone "2026" reads as a thing
 * that happened once in 2026 — which is why `single` exists to say exactly
 * that, and why everything else without an end now says "sinds".
 */
export function period({ from, to, single }) {
  const start = niceDate(from);
  if (!from) return "";
  if (single) return start;
  if (!to) return String(from).includes("-") ? `${start} — nu` : `sinds ${start}`;
  return `${start} — ${niceDate(to)}`;
}
